import React, { useState, useMemo, useRef } from 'react';
import { CountryOptionType } from '../../../utils/types';
import { Icon, Title } from '..';
import plFlag from '../../../images/flags/pl.svg';
import gbFlag from '../../../images/flags/gb.svg';

type CountrySelectProps = {
    name: string;
    onSelect: (name: string, option: CountryOptionType) => void;
    value?: number | string;
    label?: string;
    placeholder?: string;
    error?: string | null;
    className?: string;
    isDisabled?: boolean;
};

type SupportedCountryCode = 'GB' | 'PL';

type FlagOption = CountryOptionType & {
    src: string;
    alt: string;
};

const flags: FlagOption[] = [
    { id: 'GB', src: gbFlag, alt: 'UK flag' },
    { id: 'PL', src: plFlag, alt: 'Polish flag' },
];

const CountrySelect = ({
    name,
    onSelect,
    value,
    label,
    placeholder,
    error,
    className,
    isDisabled = false,
}: CountrySelectProps) => {
    const [showOptions, setShowOptions] = useState(false);
    const elementId = `select-${name}`;
    const list = useRef<HTMLDivElement | null>(null);

    const selectedOption = useMemo(() => {
        if (typeof value !== 'string') {
            return null;
        }
        const selectedFlag = flags.find(flag => flag.id === value);

        if (!selectedFlag) {
            return null;
        }

        return (
            <img
                src={selectedFlag.src}
                alt={selectedFlag.alt}
                width={24}
                height={16}
            />
        );
    }, [value]);

    const showOptionsHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        setShowOptions(!showOptions);
    };

    const closeOptionsHandler = (
        event: React.FocusEvent<HTMLButtonElement>
    ) => {
        if (event.currentTarget === event.target) {
            setShowOptions(false);
        }
    };

    const handleOnSelect = (option: CountryOptionType) => {
        onSelect(name, option);
        setShowOptions(false);
    };

    const renderFlag = (src: string, alt: string) => {
        return <img src={src} alt={alt} width={24} height={16} />;
    };

    const renderOptions = () => {
        return (
            <div className="select-menu" ref={list}>
                {flags.map((flag, index) => (
                    <div
                        className="select-option"
                        key={`${flag.id}_${index}`}
                        onClick={() => handleOnSelect(flag)}
                    >
                        {renderFlag(flag.src, flag.alt)}
                        {flag.id === value && (
                            <Icon
                                name="bx-check"
                                color="#5b956d"
                                id="selected-option"
                            />
                        )}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div
            className={`country-select-input-container ${className}`}
            id={elementId}
        >
            {label && (
                <label>
                    <Title text={label} size="s" />
                </label>
            )}

            <button
                className={`select-input clickable ${error ? 'error' : ''}`}
                onClick={showOptionsHandler}
                onBlur={closeOptionsHandler}
                disabled={isDisabled}
                name={name}
            >
                <>
                    {selectedOption || (
                        <span className="select-placeholder">
                            {placeholder || 'Select...'}
                        </span>
                    )}
                    <Icon
                        name="bx-caret-down"
                        color="#dbdde0"
                        cursor="pointer"
                    />
                    {showOptions && renderOptions()}
                </>
            </button>
            {error && <span className="error-message">{error}</span>}
        </div>
    );
};

export default CountrySelect;
